/** @jsxImportSource @emotion/react */
import React from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, Spin } from 'antd';
import { ChartDataItem, StackedSeriesItem } from '../../services/statistics.service';
import {
  overviewCardStyle,
  overviewCardHeaderStyle,
  overviewChartAreaStyle,
  overviewTrendHeaderStyle,
  useOverviewChartSize,
} from './accessOverviewLayout';

export interface AccessOverviewChartsProps {
  loading?: boolean;
  totalAccess?: number;
  featureChartData?: ChartDataItem[];
  trendChartData?: {
    isDaily: boolean;
    categories: string[];
    series: StackedSeriesItem[];
  };
}

const FEATURE_COLORS: Record<string, string> = {
  'Trang chủ': '#5B7BFE',
  'Theo dõi sức khỏe': '#54C882',
  'Tư vấn sức khỏe': '#FA983A',
  'Kiểm tra & Lên bữa ăn': '#20D4D2',
  'Nhật ký sức khỏe': '#00D0FF',
  'Mục tiêu của bạn': '#8B5CF6',
  'Cẩm nang cho bạn': '#FEDC77',
  'Khác': '#9CA3AF',
};

const COLOR_PALETTE = [
  '#5B7BFE',
  '#54C882',
  '#FA983A',
  '#20D4D2',
  '#00D0FF',
  '#8B5CF6',
  '#FEDC77',
  '#9CA3AF',
];

const formatNumber = (num: number) => num.toLocaleString('vi-VN');

export const FeatureDoughnutChartCard: React.FC<{
  loading?: boolean;
  totalAccess?: number;
  featureChartData?: ChartDataItem[];
}> = ({ loading = false, totalAccess = 0, featureChartData = [] }) => {
  const { ref, width, height } = useOverviewChartSize();

  const doughnutOption = React.useMemo(() => {
    const chartWidth = width || 539;
    const chartHeight = height || 400;
    const scale = Math.max(0.85, Math.min(chartWidth / 539, chartHeight / 400));
    const itemsPerRow = chartWidth < 460 ? 2 : 4;
    const legendRows = Math.ceil(featureChartData.length / itemsPerRow);
    const legendRowHeight = 24 * scale;
    const outerRadius = Math.max(1, Math.min(
      chartWidth * 0.238,
      (chartHeight - legendRows * legendRowHeight - 72 * scale) / 2,
    ));
    const centerY = outerRadius + 24 * scale;
    const totalLabel = `${formatNumber(totalAccess)} lượt`;

    return {
      title: {
        text: totalLabel,
        left: 'center',
        top: centerY - 16 * scale,
        padding: 0,
        textStyle: {
          fontSize: Math.min(24 * scale, outerRadius * 1.6 / (totalLabel.length * 0.6)),
          fontWeight: 600,
          color: '#262626',
          lineHeight: 32 * scale,
        },
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) =>
          `<b>${params.name}</b><br/>Lượt truy cập: ${formatNumber(params.value)} (${params.percent}%)`,
      },
      legend: Array.from({ length: legendRows }, (_, row) => ({
        data: featureChartData.slice(row * itemsPerRow, (row + 1) * itemsPerRow).map(item => item.name),
        orient: 'horizontal',
        top: chartHeight - (legendRows - row) * legendRowHeight - 40 * scale,
        left: 'center',
        itemGap: 12 * scale,
        itemWidth: 8 * scale,
        itemHeight: 8 * scale,
        icon: 'circle',
        textStyle: {
          fontSize: 12 * scale,
          color: '#595959',
        },
      })),
      series: [
        {
          name: 'Chức năng',
          type: 'pie',
          radius: [outerRadius * 0.8, outerRadius],
          center: ['50%', centerY],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 0,
            borderColor: '#fff',
            borderWidth: 0,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: (params: any) =>
              params.percent > 4 ? `${params.percent}%\n(${formatNumber(params.value)})` : '',
            rotate: 'tangential',
            fontSize: Math.max(8, 8 * scale),
            color: '#fff',
            fontWeight: '600',
            lineHeight: 10 * scale,
          },
          data: featureChartData.map((item, idx) => ({
            name: item.name,
            value: item.value,
            itemStyle: {
              color: FEATURE_COLORS[item.name] || COLOR_PALETTE[idx % COLOR_PALETTE.length],
            },
          })),
        },
      ],
    };
  }, [width, height, totalAccess, featureChartData]);

  return (
    <Card css={overviewCardStyle}>
      <Spin spinning={loading}>
        <div css={overviewCardHeaderStyle}>
          Lượt truy cập theo chức năng
        </div>
        <div ref={ref} css={overviewChartAreaStyle}>
          <ReactECharts
            option={doughnutOption}
            opts={{ devicePixelRatio: Math.max(2, window.devicePixelRatio || 1) }}
            style={{ height: '100%', width: '100%' }}
            notMerge={true}
          />
        </div>
      </Spin>
    </Card>
  );
};

export const TrendLineChartCard: React.FC<{
  loading?: boolean;
  period?: string;
  trendChartData?: {
    isDaily: boolean;
    categories: string[];
    series: StackedSeriesItem[];
  };
}> = ({ loading = false, period, trendChartData = { isDaily: true, categories: [], series: [] } }) => {
  const { ref, width } = useOverviewChartSize();

  const lineOption = React.useMemo(() => {
    const categories = trendChartData.categories || [];
    const hasData = categories.length > 0;
    const showAllCategories = period === 'this_year' || period === 'last_12_months' || period === 'last_3_years';
    const isDailyRange = period === 'this_month' || period === 'last_30_days';
    const hasZoom = !showAllCategories && categories.length > 10;
    const zoomEndValue = isDailyRange ? categories.length - 1 : 9;
    const chartWidth = width || 1135;
    const scale = Math.max(0.85, chartWidth / 1135);
    const itemsPerRow = chartWidth < 700 ? 2 : chartWidth < 1000 ? 4 : 8;
    const legendRows = Math.ceil(trendChartData.series.length / itemsPerRow);
    const legendBottom = (hasZoom ? 38 : 16) * scale;
    const legendRowHeight = 24 * scale;

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: any[]) => {
          if (!params || params.length === 0) return '';
          let total = 0;
          params.forEach((p) => (total += Number(p.value) || 0));
          let html = `<b>${params[0]?.name || ''}</b> (Tổng: ${formatNumber(total)})<br/>`;
          params.forEach((p) => {
            const val = Number(p.value) || 0;
            if (val > 0) {
              html += `${p.marker} ${p.seriesName}: <b>${formatNumber(val)}</b><br/>`;
            }
          });
          return html;
        },
      },
      legend: Array.from({ length: legendRows }, (_, row) => ({
        data: trendChartData.series.slice(row * itemsPerRow, (row + 1) * itemsPerRow).map(item => item.name),
        bottom: legendBottom + (legendRows - row - 1) * legendRowHeight,
        left: 'center',
        icon: 'rect',
        itemWidth: 16 * scale,
        itemHeight: 2 * scale,
        itemGap: 8 * scale,
        textStyle: {
          fontSize: 12 * scale,
          color: '#595959',
        },
      })),
      grid: {
        left: 16 * scale,
        right: 24 * scale,
        bottom: legendBottom + legendRows * legendRowHeight + 36 * scale,
        top: 16 * scale,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: hasData ? categories : ['Không có dữ liệu'],
        axisLabel: {
          interval: showAllCategories ? 0 : 'auto',
          showMinLabel: isDailyRange ? true : undefined,
          showMaxLabel: isDailyRange ? true : undefined,
          fontSize: 12 * scale,
          alignMinLabel: 'left',
          alignMaxLabel: 'right',
          rotate: 0,
          formatter: (val: string) => {
            if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
              const [y, m, d] = val.split('-');
              return `${d}-${m}-${y}`;
            }
            return val;
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#e8e8e8',
          },
        },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLabel: {
          fontSize: 12 * scale,
          formatter: (val: number) => formatNumber(val),
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: '#e8e8e8',
          },
        },
      },
      dataZoom: hasZoom
        ? [
            {
              type: 'slider',
              show: true,
              startValue: 0,
              endValue: zoomEndValue,
              height: 18 * scale,
              bottom: 8 * scale,
              borderColor: 'transparent',
              backgroundColor: '#f5f5f5',
              fillerColor: 'rgba(24, 144, 255, 0.2)',
              handleStyle: {
                color: '#1890ff',
                borderColor: '#1890ff',
              },
            },
            {
              type: 'inside',
              startValue: 0,
              endValue: zoomEndValue,
            },
          ]
        : [],
      series: trendChartData.series.map((s, idx) => ({
        name: s.name,
        type: 'line',
        smooth: false,
        symbol: 'circle',
        symbolSize: 6 * scale,
        lineStyle: { width: 2 * scale },
        label: {
          show: true,
          position: 'top',
          fontSize: 8 * scale,
          color: FEATURE_COLORS[s.name] || COLOR_PALETTE[idx % COLOR_PALETTE.length],
          formatter: (params: { value: number }) => formatNumber(params.value),
        },
        labelLayout: { hideOverlap: true },
        itemStyle: {
          color: FEATURE_COLORS[s.name] || COLOR_PALETTE[idx % COLOR_PALETTE.length],
        },
        data: s.data,
      })),
    };
  }, [width, period, trendChartData]);

  return (
    <Card css={overviewCardStyle}>
      <Spin spinning={loading}>
        <div css={[overviewCardHeaderStyle, overviewTrendHeaderStyle]}>
          Lượt truy cập theo thời gian
        </div>
        <div ref={ref} css={overviewChartAreaStyle}>
          <ReactECharts
            option={lineOption}
            opts={{ devicePixelRatio: Math.max(2, window.devicePixelRatio || 1) }}
            style={{ height: '100%', width: '100%' }}
            notMerge={true}
          />
        </div>
      </Spin>
    </Card>
  );
};
